import { v4 } from 'uuid';

// eslint-disable-next-line no-restricted-imports
import { CmsConfiguration } from 'cms/cms.configuration';
import {
  StoreSummary, StoreLanguage, ContinentStores, StoresMap, ContinentStoresConfig, GroupStore, StoreCatalog,
  SalesStatusTypes
} from 'idt-ecommerce-api/stores/stores.models';


export class StoresHelpers {

  public static getStoreByCountryCode(stores: StoreSummary[], storeCountryCode: string): StoreSummary | undefined {
    return stores.find((store) => store.countryCode === storeCountryCode.toUpperCase());
  }

  public static getSupportedLanguageByCode(store: StoreSummary, languageCode: string): StoreLanguage | undefined {
    return store.supportedLanguages.find((language) => language.code === languageCode);
  }

  /**
 * Devuelve el listado de idiomas distintos a nivel del campo code.
 * En caso de existir varios idiomas con el mismo code se devuelve el primero.
 * Por tanto no se diferencia entre español de españa y español de méxico porque aunque su id es distinto,
 * tienen el mismo code.
 * @param stores Listado de tiendas
 */
  public static getAllLanguagesByCode(stores: StoreSummary[]): StoreLanguage[] {
    const allLanguages: StoreLanguage[] = [];

    for (const store of stores) {
      const languages = store.supportedLanguages;

      for (const language of languages) {
        const found = allLanguages.find((l) => { // avoid duplicates by code
          return language.code === l.code;
        });

        if (!found) {
          allLanguages.push(language);
        }
      }
    }
    return allLanguages;
  }

  /**
   * This method gets all continent stores.
   * It can return them split by sale/no-sale, continents defined in the config, or the global continents
   */
  public static getGroupStores(config: CmsConfiguration, stores: StoreSummary[]): GroupStore[] {
    if (config.groupStoresBySaleStatus) {
      const saleStores: ContinentStores[] = [];
      const noSaleStores: ContinentStores[] = [];
      const wwStores: ContinentStores[] = [];
      for (const store of stores) {
        const catalog = store.catalogs.find((ct) => ct.type === '1');
        let isNoSale = false;
        if (catalog) {
          let relevantSet: ContinentStores[];
          if (store.continent === '-1') {
            relevantSet = wwStores;
          } else if (store.isOpenForSale) {
            relevantSet = saleStores;
          } else {
            relevantSet = noSaleStores;
            isNoSale = true;
          }
          const continent = this.getOrCreateCatalog(relevantSet, catalog, isNoSale);
          continent.stores.push(store);
        }
      }
      return [
        { id: SalesStatusTypes.SALE, name: 'Mercados de VENTA', continents: saleStores },
        { id: SalesStatusTypes.NO_SALE, name: 'Mercados de NO VENTA', continents: noSaleStores },
        { id: SalesStatusTypes.WORLD_WIDE, name: 'World Wide', continents: wwStores },
      ];
    }

    const continents = config.storesGroup != null
      ? StoresHelpers.getStoresGroups(stores, config.storesGroup)
      : StoresHelpers.getAllContinents(stores);
    return [{ id: SalesStatusTypes.WORLD, name: 'World', continents }];
  }

  /**
   * This method gets all continent stores.
   * It doesn't split them by any group
   * This is meant to replace calling the getStoresGroups and getAllContinents functions manually
   * It calls both of those functions through getGroupStores under the hood
   */
  public static getContinents(config: CmsConfiguration, stores: StoreSummary[]) {
    return this.getGroupStores(config, stores).flatMap((group) => group.continents);
  }

  public static getStoresMap(stores: StoreSummary[]): StoresMap {
    const storesMap: StoresMap = {};

    stores.forEach((country) => {
      storesMap[country.countryCode] = country;
    });
    return storesMap;
  }


  private static getAllContinents(stores: StoreSummary[]): ContinentStores[] {
    const allContinents: ContinentStores[] = [
      { id: '1', name: 'Africa', nameES: 'África', stores: [] },
      { id: '2', name: 'America', nameES: 'América', stores: [] },
      { id: '3', name: 'Asia', nameES: 'Asia', stores: [] },
      { id: '4', name: 'Europe', nameES: 'Europa', stores: [] },
      { id: '5', name: 'Oceania', nameES: 'Oceanía', stores: [] },
      { id: '6', name: 'Antarctica', nameES: 'Antártida', stores: [] },
      { id: '7', name: 'Middle East', nameES: 'Oriente medio', stores: [] }
    ];

    const otherStores: StoreSummary[] = [];

    stores.forEach((store) => {
      if (store.continent) {
        const index = allContinents.findIndex((continent) => {
          return continent.id === store.continent;
        });

        if (index !== -1) {
          allContinents[index].stores.push(store);
        } else {
          otherStores.push(store);
        }
      }
    });

    const continents: ContinentStores[] = [];
    for (const continent of allContinents) {
      if (continent.stores.length > 0) {
        continents.push(continent);
      }
    }
    if (otherStores.length > 0) {
      continents.push({ id: '8', name: 'Others', nameES: 'Otros', stores: otherStores });
    }

    return continents;
  }

  private static getStoresGroups(
    stores: StoreSummary[],
    storesGroupConfig: ContinentStoresConfig[]
  ): ContinentStores[] {
    if (!stores.length) {
      return [];
    }

    const parsedStoresGroup: ContinentStores[] = storesGroupConfig.map(
      (storeGroup) => {
        const storesList: StoreSummary[] = storeGroup.stores.map((storeId: number) => {
          return stores.find((it) => storeId === Number.parseInt(it.id, 10));
        }).filter(isDefined);
        return {
          id: storeGroup.id,
          name: storeGroup.name,
          nameES: storeGroup.nameES,
          stores: storesList
        };
      }
    );

    const flatStores: StoreSummary[] = parsedStoresGroup.reduce((prev: StoreSummary[], current) => {
      return prev.concat(current.stores);
    }, []);

    const others = stores.filter((store) => {
      return !flatStores.find((usedStore) => usedStore.id === store.id);
    });

    parsedStoresGroup.push({
      id: v4(),
      name: 'Others',
      nameES: 'Otros',
      stores: others
    });

    return parsedStoresGroup;
  }

  /**
   * Gets the continent from the array. Creating and pushing it into the array if it doesn't exist
   */
  private static getOrCreateCatalog(
    continents: ContinentStores[],
    catalog: StoreCatalog,
    isNoSale: boolean
  ): ContinentStores {
    let foundContinent = continents.find((continent) => continent.id === catalog.id);
    if (foundContinent) {
      return foundContinent;
    }
    const identifier = catalog.identifier ?? '';
    const name = isNoSale ? `NV - ${identifier}` : identifier;
    foundContinent = { id: catalog.id, name, nameES: name, stores: [] };
    continents.push(foundContinent);
    return foundContinent;
  }

}
